import React from "react";
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  EnvelopeIcon,
  LinkIcon,
  ChatBubbleBottomCenterTextIcon,
  PencilSquareIcon,
  PhoneIcon,
} from "@heroicons/react/20/solid";
import {
  formatDateOnly,
  originColor,
  relativeTime,
  leadDesgloseTotal,
} from "../../utils/leadHelpers";

/* Inner content of a lead card — shared by the kanban board and the DragOverlay.
   The outer chrome (border, background, drag handlers) stays at the call site. */
export default function LeadCard({
  form,
  isViewer,
  linkedEvent,
  menuSlot,
  nombreEspacio,
  // Cuántas oportunidades MÁS tiene el mismo cliente: { eventos, leads }
  // aparte de este lead. Se muestra como contador, sin adjetivos.
  recurrencia = null,
}) {
  const otras = (recurrencia?.eventos || 0) + (recurrencia?.leads || 0);
  const detalleOtras = [
    recurrencia?.eventos ? `${recurrencia.eventos} ${recurrencia.eventos === 1 ? "evento" : "eventos"}` : null,
    recurrencia?.leads ? `${recurrencia.leads} ${recurrencia.leads === 1 ? "lead más" : "leads más"}` : null,
  ]
    .filter(Boolean)
    .join(" y ");
  const total = leadDesgloseTotal(form);
  const isCancelled = linkedEvent?.status === "cancelado";
  // Espacios de interés. Solo se pintan si el venue los maneja (el call site
  // pasa el resolvedor); sin selección no se muestra nada — "cualquiera" es
  // el estado normal de un lead recién llegado, no una alerta.
  const espaciosAll = nombreEspacio ? form.espaciosIds || [] : [];
  const espacios = espaciosAll.slice(0, 3);
  const espaciosExtra = espaciosAll.length - espacios.length;

  return (
    <>
      {/* Header: name + topic, value + badge/menu */}
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0 flex-1">
          {/* Cliente y nombre del lead, del mismo tamaño: ninguno es el
              subtítulo del otro. El contador dice cuántas oportunidades
              más tiene este cliente, sin etiquetas. */}
          <div className="text-xs font-semibold text-zinc-800 dark:text-zinc-100 leading-snug truncate">
            {form.name || "Sin nombre"}
            {otras > 0 && (
              <span
                className="ml-1.5 inline-flex items-center rounded-full border border-zinc-200 dark:border-zinc-600 px-1.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-400 align-middle"
                title={`Este cliente tiene ${detalleOtras}`}
              >
                +{otras}
              </span>
            )}
          </div>
          {form.topic && (
            <div className="text-xs font-medium text-zinc-600 dark:text-zinc-300 mt-0.5 truncate">
              {form.topic}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {total > 0 && (
            <span className="text-xs font-semibold text-[#1b4d2e] dark:text-emerald-400 whitespace-nowrap">
              ${total.toLocaleString("es-MX", { maximumFractionDigits: 0 })}
              {form.ivaEnabled ? " +IVA" : ""}
            </span>
          )}
          {form.convertedToEventId ? (
            <span
              className={`flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                isCancelled
                  ? "text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30"
                  : "text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30"
              }`}
            >
              <LinkIcon className="h-3 w-3" />
              {isCancelled ? "Evento cancelado" : "Evento"}
            </span>
          ) : (
            menuSlot || null
          )}
        </div>
      </div>

      {/* Contact rows */}
      {(form.phone || form.userEmail) && (
        <div className="mt-1.5 space-y-0.5">
          {form.phone && (
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
              <PhoneIcon className="h-3 w-3 shrink-0 text-zinc-400 dark:text-zinc-500" />
              <span className="truncate">{form.phone}</span>
            </div>
          )}
          {form.userEmail && (
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
              <EnvelopeIcon className="h-3 w-3 shrink-0 text-zinc-400 dark:text-zinc-500" />
              <span className="truncate">{form.userEmail}</span>
            </div>
          )}
        </div>
      )}

      {/* Message (from website forms) */}
      {form.message && (
        <div className="flex items-start gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 mt-1.5 leading-relaxed">
          <ChatBubbleBottomCenterTextIcon className="h-3 w-3 shrink-0 mt-0.5 text-zinc-400 dark:text-zinc-500" />
          <p className="line-clamp-2 min-w-0">{form.message}</p>
        </div>
      )}

      {/* Notes (hidden for viewers) */}
      {form.notes && !isViewer && (
        <div className="flex items-start gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 mt-1.5 leading-relaxed">
          <PencilSquareIcon className="h-3 w-3 shrink-0 mt-0.5 text-zinc-400 dark:text-zinc-500" />
          <p className="line-clamp-2 min-w-0">{form.notes}</p>
        </div>
      )}

      {/* Info pills row */}
      {(form.interestDates?.length > 0 ||
        form.cotizacionHistory?.length > 0 ||
        espacios.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {espacios.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-0.5 text-[10px] font-medium text-[#1b4d2e] bg-[#1b4d2e1a] px-1.5 py-0.5 rounded dark:text-emerald-400 dark:bg-emerald-900/30"
            >
              <Squares2X2Icon className="h-3 w-3" />
              {nombreEspacio(id)}
            </span>
          ))}
          {espaciosExtra > 0 && (
            <span className="text-[10px] font-medium text-zinc-400">
              +{espaciosExtra}
            </span>
          )}
          {form.interestDates?.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded dark:text-blue-400 dark:bg-blue-900/30">
              <CalendarDaysIcon className="h-3 w-3" />
              {form.interestDates.length} fecha{form.interestDates.length > 1 ? "s" : ""}
            </span>
          )}
          {form.cotizacionHistory?.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded dark:text-purple-400 dark:bg-purple-900/30">
              <DocumentTextIcon className="h-3 w-3" />
              {form.cotizacionHistory.length} cot.
            </span>
          )}
        </div>
      )}

      {/* Footer: origin + relative time */}
      <div className="flex items-center justify-between gap-2 mt-2 pt-1.5 border-t border-zinc-100 dark:border-zinc-700/40">
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium truncate ${originColor(form.origin)}`}>
          {form.origin || "Venueboard"}
        </span>
        <span
          className="text-[10px] text-zinc-400 whitespace-nowrap shrink-0"
          title={form.createdAt?.toDate ? formatDateOnly(form.createdAt.toDate()) : undefined}
        >
          {form.createdAt?.toDate ? relativeTime(form.createdAt) : "-"}
        </span>
      </div>
    </>
  );
}
